"use client";

import type { DayEntry, MessageGroup } from "@/features/chat/Types/messages.ts";
import type { ConversationData } from "@/features/chat/Types/conversations.ts";
import type React from "react";

import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { parseJSON } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { LayoutGroup, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { AlertCircle, Check, Edit2, ExternalLink, Loader2, Save, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ReactPlayer from "react-player";

import { MediaGrid } from "@/features/chat/components/ImageGrid";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import useUserStore from "@/features/auth/store/useUserStore";
import { STUDENT_WORKSPACE_PATHS, TUTOR_WORKSPACE_PATHS } from "@/routes/paths";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ConversationViewProps {
  messages: any;
  isLoading: boolean;
  editingMessageId: string | null;
  editingPendingMessageId: string | null;
  editMessageContent: string;
  scrollAreaRef: RefObject<HTMLDivElement>;
  editTextareaRef: RefObject<HTMLTextAreaElement>;
  currentUser: any;
  isCurrentUser: (userId: string) => boolean;
  formatFileSize: (size: number) => string;
  startEditingMessage: (messageId: string, content: string) => void;
  startEditingPendingMessage: (messageId: string, content: string) => void;
  cancelEditing: () => void;
  saveEditedMessage: () => void;
  deleteMessage: (messageId: string) => void;
  removePendingMessage: (messageId: string) => void;

  setEditMessageContent: (content: string) => void;
  handleEditKeyPress: (e: React.KeyboardEvent) => void;
  activeConversation: ConversationData | null;
  animatedMessageIds?: Set<string>;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  scrollToBottom: () => void;
  checkIfNearBottom: () => boolean;
  renderSpecialGroupsAsNormal?: boolean; // New prop to control rendering of special groups
  mode?: "full" | "compact"; // New prop for display mode
  disableMessageActions?: boolean; // New prop to disable message interactions
  onGroupSelect?: (group: any) => void; // New prop for group selection in popup mode
  isSpecific?: boolean; // New prop to indicate if viewing a specific group
}

export function ConversationView({
  activeConversation,
  messages,
  isLoading,
  // Remove pendingMessages from destructuring
  editingMessageId,
  editingPendingMessageId,
  editMessageContent,
  scrollAreaRef,
  editTextareaRef,
  currentUser,
  isCurrentUser,
  startEditingMessage,
  startEditingPendingMessage,
  cancelEditing,
  saveEditedMessage,
  deleteMessage,
  removePendingMessage,

  setEditMessageContent,
  handleEditKeyPress,
  animatedMessageIds = new Set(),
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  scrollToBottom,
  checkIfNearBottom,
  renderSpecialGroupsAsNormal = false, // Default to false to maintain backward compatibility
  mode = "full", // Default to full mode
  disableMessageActions = false, // Default to false to maintain backward compatibility
  onGroupSelect, // New prop for group selection in popup mode
  isSpecific = false, // Default to false
}: ConversationViewProps) {
  const { t, i18n } = useTranslation(["common", "chat", "classroom"]);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { user } = useUserStore();
  const userRole = user?.role;
  const userTimezone = useUserStore((state) => state.user?.timezone) || "UTC";

  // Simple cache for link preview metadata to avoid refetching
  const linkPreviewCacheRef = useRef<Map<string, any>>(new Map());

  // Track previous message count to detect new messages
  const prevMessageCountRef = useRef<number>(0);

  // Reference to the messages container for event delegation
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // State to track the latest message from the current user
  const [, setLatestUserMessageId] = useState<string | null>(null);

  // State to track the last read message by other users
  const [, setLastReadMessageId] = useState<string | null>(null);

  // Add a set to track processing actions to prevent double-clicks
  const processingActionsRef = useRef<Set<string>>(new Set());

  // Find the latest message from the current user and the last read message
  useEffect(() => {
    if (!messages || isLoading || !currentUser) return;

    let latestUserMsg: any = null;
    let latestUserMsgTimestamp = 0;
    let lastReadMsg: any = null;
    let lastReadMsgTimestamp = 0;

    // Iterate through all messages to find the latest from the current user
    messages.data.forEach((dayEntry: DayEntry) => {
      dayEntry.groups.forEach((group) => {
        // Handle special groups with subgroups
        if (group.special_tag && group.subgroups) {
          group.subgroups.forEach((subgroup: any) => {
            const isFromCurrentUser = isCurrentUser(subgroup.user.id);

            subgroup.messages.forEach((msg: any) => {
              const msgTimestamp = new Date(msg.created_at).getTime();

              // Check if this is a message from the current user and is newer than our current latest
              if (isFromCurrentUser && msgTimestamp > latestUserMsgTimestamp) {
                latestUserMsg = msg;
                latestUserMsgTimestamp = msgTimestamp;
              }

              // Check if this message has been read by other users and is newer than our current last read
              if (!isFromCurrentUser && msg.is_read && msgTimestamp > lastReadMsgTimestamp) {
                lastReadMsg = msg;
                lastReadMsgTimestamp = msgTimestamp;
              }
            });
          });
        } else {
          // Handle regular groups
          const isFromCurrentUser = isCurrentUser(group.messages.user.id);

          group.messages.messages.forEach((msg) => {
            const msgTimestamp = parseJSON(msg.created_at).getTime();

            // Check if this is a message from the current user and is newer than our current latest
            if (isFromCurrentUser && msgTimestamp > latestUserMsgTimestamp) {
              latestUserMsg = msg;
              latestUserMsgTimestamp = msgTimestamp;
            }

            // Check if this message has been read by other users and is newer than our current last read
            if (!isFromCurrentUser && msg.is_read && msgTimestamp > lastReadMsgTimestamp) {
              lastReadMsg = msg;
              lastReadMsgTimestamp = msgTimestamp;
            }
          });
        }
      });
    });

    // Update state with the latest message ID from current user
    setLatestUserMessageId(latestUserMsg?.id || null);

    // Update state with the last read message ID
    setLastReadMessageId(lastReadMsg?.id || null);
  }, [messages, currentUser, isCurrentUser, isLoading]);

  // Remove the getPendingMessages function and update the event handler
  useEffect(() => {
    const handleMessageActions = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Find the closest button if the event is from a child of a button
      const button = target.closest("button");

      if (!button) return;

      // Get the message container
      const messageContainer = button.closest("[data-message-id]");

      if (!messageContainer) return;

      // Extract message ID
      const messageId = messageContainer.getAttribute("data-message-id");

      if (!messageId) return;

      // Create a unique action key
      let actionKey = "";

      // Determine action type based on button attributes
      if (button.classList.contains("edit-button")) {
        actionKey = `edit-${messageId}`;
        if (processingActionsRef.current.has(actionKey)) return;
        processingActionsRef.current.add(actionKey);

        // Check if this is a pending message by looking for it in the messages data
        let isPendingMessage = false;
        let pendingMessageContent = "";

        if (messages && messages.data) {
          messages.data.forEach((dayEntry: any) => {
            dayEntry.groups.forEach((group: any) => {
              if (group.special_tag && group.subgroups) {
                group.subgroups.forEach((subgroup: any) => {
                  subgroup.messages.forEach((msg: any) => {
                    if (msg.id === messageId && msg.pending) {
                      isPendingMessage = true;
                      pendingMessageContent = msg.message;
                    }
                  });
                });
              } else {
                if (group.messages) {
                  group.messages.messages.forEach((msg: any) => {
                    if (msg.id === messageId && msg.pending) {
                      isPendingMessage = true;
                      pendingMessageContent = msg.message;
                    }
                  });
                }
              }
            });
          });
        }

        if (isPendingMessage) {
          // This is a pending message - use pending message editing
          startEditingPendingMessage(messageId, pendingMessageContent);
        } else {
          // This is a regular message - find the message content
          const messageElement = messageContainer.querySelector(".message-content");
          const messageContent = messageElement?.textContent || "";

          startEditingMessage(messageId, messageContent);
        }

        // Clear the processing flag after a short delay
        setTimeout(() => {
          processingActionsRef.current.delete(actionKey);
        }, 500);
      } else if (button.classList.contains("delete-button")) {
        actionKey = `delete-${messageId}`;
        if (processingActionsRef.current.has(actionKey)) return;
        processingActionsRef.current.add(actionKey);

        deleteMessage(messageId);

        // Keep the flag longer for delete to prevent double-clicks
        setTimeout(() => {
          processingActionsRef.current.delete(actionKey);
        }, 2000);
      } else if (button.classList.contains("remove-pending-button")) {
        removePendingMessage(messageId);
      } else if (button.classList.contains("edit-pending-button")) {
        // Find the pending message content
        let pendingMessageContent = "";

        if (messages && messages.data) {
          messages.data.forEach((dayEntry: any) => {
            dayEntry.groups.forEach((group: any) => {
              if (group.special_tag && group.subgroups) {
                group.subgroups.forEach((subgroup: any) => {
                  subgroup.messages.forEach((msg: any) => {
                    if (msg.id === messageId && msg.pending) {
                      pendingMessageContent = msg.message;
                    }
                  });
                });
              } else {
                if (group.messages) {
                  group.messages.messages.forEach((msg: any) => {
                    if (msg.id === messageId && msg.pending) {
                      pendingMessageContent = msg.message;
                    }
                  });
                }
              }
            });
          });
        }

        if (pendingMessageContent) {
          startEditingPendingMessage(messageId, pendingMessageContent);
        }
      }
    };

    // Add event listener to the messages container
    const container = messagesContainerRef.current;

    if (container) {
      container.addEventListener("click", handleMessageActions);
    }

    // Cleanup
    return () => {
      if (container) {
        container.removeEventListener("click", handleMessageActions);
      }
    };
  }, [
    startEditingMessage,
    deleteMessage,
    removePendingMessage,
    startEditingPendingMessage,
    messages,
  ]);

  // Check for new messages and auto-scroll if needed
  useEffect(() => {
    if (!messages || isLoading) {
      prevMessageCountRef.current = 0;

      return;
    }

    // Count total messages
    let totalMessages = 0;

    messages.data.forEach((dayEntry: DayEntry) => {
      dayEntry.groups.forEach((group) => {
        if (group.special_tag && group.subgroups) {
          // Count messages in subgroups for special groups
          group.subgroups.forEach((subgroup: any) => {
            totalMessages += subgroup.messages.length;
          });
        } else {
          // Count messages in regular groups
          totalMessages += group.messages.messages.length;
        }
      });
    });

    // If we have more messages than before, and we're near the bottom, scroll to bottom
    if (totalMessages > prevMessageCountRef.current && checkIfNearBottom()) {
      // Small delay to ensure new messages are rendered
      setTimeout(scrollToBottom, 100);
    }

    // Update the ref
    prevMessageCountRef.current = totalMessages;
  }, [messages, isLoading, checkIfNearBottom, scrollToBottom]);

  // Auto-scroll to center when editing a message
  useEffect(() => {
    if (editingMessageId || editingPendingMessageId) {
      const messageId = editingMessageId || editingPendingMessageId;
      const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);

      if (messageElement && scrollAreaRef.current) {
        // Small delay to ensure the edit form is rendered
        setTimeout(() => {
          messageElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }, 100);
      }
    }
  }, [editingMessageId, editingPendingMessageId]);

  // Refs for scroll position tracking
  const scrollPositionRef = useRef<number>(0);
  const prevScrollHeightRef = useRef<number>(0);
  const isAdjustingScrollRef = useRef<boolean>(false);

  // Handle scroll events for infinite scrolling
  const handleScroll = useCallback(() => {
    if (!scrollAreaRef.current || isAdjustingScrollRef.current) return;

    const scrollContainer = scrollAreaRef.current.querySelector(
      "[data-radix-scroll-area-viewport]",
    );

    if (!scrollContainer) return;

    // Store current scroll position
    scrollPositionRef.current = scrollContainer.scrollTop;

    // Check if we're near the bottom
    checkIfNearBottom();

    // Check if we've scrolled near the top (with a threshold of 100px)
    // Don't fetch more pages when viewing a specific group
    if (scrollContainer.scrollTop < 100 && hasNextPage && !isFetchingNextPage && !isSpecific) {
      scrollPositionRef.current = scrollContainer.scrollTop;
      prevScrollHeightRef.current = scrollContainer.scrollHeight;

      // Add a class to prevent animation during loading to reduce jitter
      scrollContainer.classList.add("loading-messages");

      // Set flag to prevent scroll handling during adjustment
      isAdjustingScrollRef.current = true;

      // Fetch the next page
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, checkIfNearBottom, isSpecific]);

  // Attach scroll event listener
  useEffect(() => {
    if (!scrollAreaRef.current) return;

    const scrollContainer = scrollAreaRef.current.querySelector(
      "[data-radix-scroll-area-viewport]",
    );

    if (!scrollContainer) return;

    scrollContainer.addEventListener("scroll", handleScroll);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, activeConversation?.id, isSpecific]);

  // Maintain scroll position when new messages are loaded
  useEffect(() => {
    if (!scrollAreaRef.current) return;

    const scrollContainer = scrollAreaRef.current.querySelector(
      "[data-radix-scroll-area-viewport]",
    );

    if (!scrollContainer) return;

    // When loading starts, we don't do anything
    if (isFetchingNextPage) return;

    // When loading finishes, we need to adjust the scroll position
    if (!isFetchingNextPage && isAdjustingScrollRef.current) {
      // Calculate how much the content height has changed
      const heightDifference = scrollContainer.scrollHeight - prevScrollHeightRef.current;

      // If height increased (new messages loaded at top), adjust scroll position
      if (heightDifference > 0) {
        // Set scroll position to maintain the same view
        // This keeps the user looking at the same messages they were viewing before
        scrollContainer.scrollTop = scrollPositionRef.current + heightDifference;
      }

      // Clean up
      scrollContainer.classList.remove("loading-messages");
      isAdjustingScrollRef.current = false;
    }
  }, [isFetchingNextPage]);

  const formatDate = (dateString: string) => {
    // Enhanced validation
    if (!dateString || typeof dateString !== "string" || dateString.trim() === "") {
      return t("common:today"); // Fallback to today if date is invalid
    }

    try {
      const userLocale = getDateFnsLocale(i18n.language);
      const now = new Date();

      // The date string from API is like "YYYY-MM-DD" and is in UTC.
      const messageDateUTC = new Date(`${dateString}T00:00:00Z`);

      // Check if the date is valid
      if (isNaN(messageDateUTC.getTime())) {
        console.warn(`Invalid date string received: ${dateString}`);

        return t("common:today"); // Fallback to today
      }

      // Get current date and yesterday's date in user's timezone
      const todayInUserTz = new Date(
        now.toLocaleString("en-US", { timeZone: userTimezone }),
      ).setHours(0, 0, 0, 0);
      const yesterdayInUserTz = new Date(todayInUserTz).setDate(
        new Date(todayInUserTz).getDate() - 1,
      );

      // Convert message date to user's timezone for comparison
      const messageDateInUserTz = new Date(
        messageDateUTC.toLocaleString("en-US", { timeZone: userTimezone }),
      ).setHours(0, 0, 0, 0);

      if (messageDateInUserTz === todayInUserTz) {
        return t("common:today");
      }
      if (messageDateInUserTz === yesterdayInUserTz) {
        return t("common:yesterday");
      }

      return formatInTimeZone(messageDateUTC, userTimezone, "PPP", { locale: userLocale });
    } catch (error) {
      console.error("Error formatting date:", error, "dateString:", dateString);

      return t("common:today"); // Fallback to today on any error
    }
  };

  // Helper function to format read by information
  const formatReadByInfo = (readBy: any) => {
    if (!readBy || typeof readBy !== "object" || Object.keys(readBy).length === 0) {
      return null;
    }

    const readers = Object.entries(readBy).map(([userId, timestamp]) => {
      const member = activeConversation?.members.find((m) => m.user.id === userId);
      const readTime = typeof timestamp === "string" ? new Date(timestamp) : null;

      return {
        name: member ? `${member.user.first_name} ${member.user.last_name}` : "Unknown User",
        timestamp: readTime
          ? formatInTimeZone(readTime, userTimezone, "PPp", {
              locale: getDateFnsLocale(i18n.language),
            })
          : "Unknown time",
      };
    });

    return readers;
  };

  // Utilities for link parsing and previews
  const URL_REGEX = /((https?:\/\/|www\.)[^\s<]+)/gi;

  const trimTrailingPunctuation = (url: string) => url.replace(/[),.;!?]+$/g, "");

  const normalizeUrl = (rawUrl: string) => {
    const cleaned = trimTrailingPunctuation(rawUrl);

    return cleaned.startsWith("http") ? cleaned : `https://${cleaned}`;
  };

  const isSameOrigin = (targetUrl: string) => {
    try {
      const u = new URL(targetUrl, window.location.origin);

      return u.origin === window.location.origin;
    } catch {
      return false;
    }
  };

  const extractUrls = (text: string): string[] => {
    const urls: string[] = [];

    if (!text) return urls;
    let match: RegExpExecArray | null;
    const regex = new RegExp(URL_REGEX);

    while ((match = regex.exec(text)) !== null) {
      urls.push(normalizeUrl(match[0]));
    }

    return Array.from(new Set(urls));
  };

  const LinkPreviewCard: React.FC<{ url: string }> = ({ url }) => {
    const [meta, setMeta] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      let mounted = true;
      const cached = linkPreviewCacheRef.current.get(url);

      if (cached) {
        setMeta(cached);

        return;
      }

      // If ReactPlayer can play, we don't need meta
      if (ReactPlayer.canPlay(url)) {
        setMeta({ playable: true });
        linkPreviewCacheRef.current.set(url, { playable: true });

        return;
      }

      setLoading(true);
      setError(null);

      // Fetch OpenGraph metadata directly
      const controller = new AbortController();
      const fetchMeta = async () => {
        try {
          const res = await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent(url)}`, {
            signal: controller.signal,
          });

          if (!res.ok) throw new Error("fetch_failed");
          const data = await res.json();
          const html = data.contents;

          if (!mounted) return;

          // Parse OpenGraph meta tags
          const titleMatch =
            html.match(/<meta[^>]*property="og:title"[^>]*content="([^"]*)"[^>]*>/i) ||
            html.match(/<title[^>]*>([^<]*)<\/title>/i);
          const descriptionMatch =
            html.match(/<meta[^>]*property="og:description"[^>]*content="([^"]*)"[^>]*>/i) ||
            html.match(/<meta[^>]*name="description"[^>]*content="([^"]*)"[^>]*>/i);
          const imageMatch = html.match(
            /<meta[^>]*property="og:image"[^>]*content="([^"]*)"[^>]*>/i,
          );
          const siteNameMatch = html.match(
            /<meta[^>]*property="og:site_name"[^>]*content="([^"]*)"[^>]*>/i,
          );

          const m = {
            title: titleMatch ? titleMatch[1] : new URL(url).hostname,
            author: descriptionMatch ? descriptionMatch[1] : "",
            provider: siteNameMatch ? siteNameMatch[1] : new URL(url).hostname,
            thumbnail: imageMatch ? imageMatch[1] : null,
            description: descriptionMatch ? descriptionMatch[1] : null,
            playable: false,
          };

          setMeta(m);
          linkPreviewCacheRef.current.set(url, m);
        } catch (e) {
          if (!mounted) return;
          setError("meta_failed");
        } finally {
          if (mounted) setLoading(false);
        }
      };

      fetchMeta();

      return () => {
        mounted = false;
        controller.abort();
      };
    }, [url]);

    if (ReactPlayer.canPlay(url)) {
      return (
        <div className="mt-2 overflow-hidden rounded-lg">
          <div className="bg-foreground/50 rounded-lg p-0 backdrop-blur-sm">
            <div className="relative">
              <ReactPlayer
                controls
                height="240px"
                style={{ maxWidth: "100%" }}
                url={url}
                width="100%"
              />
            </div>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="bg-foreground/50 mt-2 flex items-center gap-2 rounded-lg p-3 backdrop-blur-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-muted-foreground text-xs">{t("common:loading")}</span>
        </div>
      );
    }

    if (error || !meta) {
      // Fallback minimal card showing domain only
      try {
        const u = new URL(url);

        return (
          <div className="bg-foreground/50 mt-2 flex items-center justify-between rounded-lg p-3 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="bg-muted/20 flex items-center justify-center rounded-md p-2">
                <Icon height="1.25rem" icon="mdi:web" width="1.25rem" />
              </div>
              <div>
                <div className="text-accent-foreground text-sm font-medium">{u.hostname}</div>
                <div className="text-muted-foreground max-w-[28rem] truncate text-xs">
                  {u.pathname || "/"}
                </div>
              </div>
            </div>
            <div className="flex items-center ps-6">
              <SafeLink url={url}>{t("common:viewDetails")}</SafeLink>
            </div>
          </div>
        );
      } catch {
        return null;
      }
    }

    return (
      <div className="bg-foreground/50 mt-2 flex items-start gap-3 rounded-lg p-3 backdrop-blur-sm">
        {meta.thumbnail ? (
          <img
            alt={meta.title || "preview"}
            className="h-14 w-14 flex-shrink-0 rounded-md object-cover"
            src={meta.thumbnail}
          />
        ) : (
          <div className="bg-muted/20 flex h-14 w-14 items-center justify-center rounded-md">
            <Icon height="1.25rem" icon="mdi:link-variant" width="1.25rem" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-accent-foreground truncate text-sm font-semibold">{meta.title}</div>
          <div className="text-muted-foreground truncate text-xs">{meta.provider}</div>
          {meta.description && (
            <div className="text-muted-foreground mt-1 line-clamp-2 text-xs">
              {meta.description}
            </div>
          )}
        </div>
        <SafeLink url={url}>{t("common:viewDetails")}</SafeLink>
      </div>
    );
  };

  const SafeLink: React.FC<{ url: string; children: React.ReactNode }> = ({ url, children }) => {
    const [open, setOpen] = useState(false);

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      // If same origin, allow immediate navigation
      if (isSameOrigin(url)) return;
      e.preventDefault();
      setOpen(true);
    };

    const handleOpen = () => {
      setOpen(false);
      try {
        window.open(url, "_blank", "noopener,noreferrer");
      } catch {
        window.location.href = url;
      }
    };

    let hostname = "";

    try {
      hostname = new URL(url).hostname;
    } catch {}

    return (
      <>
        <a
          className="text-blue-400 underline-offset-2 hover:underline"
          href={url}
          rel="noreferrer noopener nofollow"
          target="_blank"
          onClick={handleClick}
        >
          {children}
        </a>

        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogContent className="mx-auto w-full max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <span className="flex items-center justify-center rounded-md p-1">
                  <Icon height="1rem" icon="mdi:shield-alert" width="1rem" />
                </span>
                {t("common:externalLink.warningTitle" as any)}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("common:externalLink.warningDescription" as any)}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="bg-foreground/50 mb-2 max-w-full overflow-hidden rounded-md p-3">
              <div className="text-muted-foreground text-xs">
                {t("common:externalLink.domain" as any)}
              </div>
              <div className="text-accent-foreground text-sm font-medium break-all">
                {hostname || url}
              </div>
              <div className="text-muted-foreground mt-1 text-xs break-all">{url}</div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={handleOpen}>
                <span className="mr-2 inline-flex items-center justify-center">
                  <Icon height="1rem" icon="mdi:open-in-new" width="1rem" />
                </span>
                {t("common:externalLink.open" as any)}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  };

  const renderMessageTextWithLinks = (text: string) => {
    if (!text || typeof text !== "string") return text;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const regex = new RegExp(URL_REGEX);
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      const raw = match[0];
      const normalized = normalizeUrl(raw);
      const start = match.index;

      if (start > lastIndex) parts.push(text.slice(lastIndex, start));
      parts.push(
        <SafeLink key={`${normalized}-${start}`} url={normalized}>
          {trimTrailingPunctuation(raw)}
        </SafeLink>,
      );
      lastIndex = start + raw.length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));

    return parts;
  };

  // Render message actions with proper class names for event delegation
  const renderMessageActions = (isUserMessage: boolean, message?: any) => {
    const isCompact = mode === "compact";
    const buttonSize = isCompact ? "h-5 w-5" : "h-6 w-6";
    const iconSize = isCompact ? "h-2.5 w-2.5" : "h-3 w-3";

    return (
      <div className="flex gap-1">
        {isUserMessage && !message.error && !disableMessageActions && (
          <>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className={cn(
                      "hover:bg-foreground active:bg-foreground text-accent-foreground edit-button rounded-md backdrop-blur-sm",
                      buttonSize,
                    )}
                    size="icon"
                    variant="ghost"
                  >
                    <Edit2 className={iconSize} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("common:edit")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className={cn(
                      "hover:bg-foreground active:bg-foreground text-destructive delete-button rounded-md backdrop-blur-sm",
                      buttonSize,
                    )}
                    size="icon"
                    variant="ghost"
                  >
                    <Trash2 className={iconSize} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side={"left"}>{t("common:remove")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        )}
      </div>
    );
  };

  // Render pending message actions
  const renderPendingMessageActions = (pendingMsg: any) => {
    return (
      <>
        {pendingMsg.status === "error" && !disableMessageActions && (
          <>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="h-7 w-7 p-0"
                    variant="ghost"
                    onClick={() => startEditingPendingMessage(pendingMsg.id, pendingMsg.content)}
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("common:edit")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="h-7 w-7 p-0"
                    variant="ghost"
                    onClick={() => removePendingMessage(pendingMsg.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("common:delete")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        )}
      </>
    );
  };

  // Helper function to render a single message
  const renderMessage = (
    message: any,
    isUserMessage: boolean,
    isFirstInGroup: boolean,
    isLastInGroup: boolean,
    user: any,
  ) => {
    // Check if this message should be animated
    const shouldAnimate = animatedMessageIds.has(message.id);

    // Ref for the message bubble (use callback ref)
    let bubbleNode: HTMLDivElement | null = null;
    let longPressTimeout: NodeJS.Timeout | null = null;

    // Handler for long-press on mobile
    const handleTouchStart = (e: React.TouchEvent) => {
      if (!isMobile) return;
      const touch = e.touches[0];

      longPressTimeout = setTimeout(() => {
        if (bubbleNode) {
          const event = new MouseEvent("contextmenu", {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: touch.clientX,
            clientY: touch.clientY,
          });

          bubbleNode.dispatchEvent(event);
        }
      }, 400);
    };
    const handleTouchEnd = () => {
      if (!isMobile) return;
      if (longPressTimeout) clearTimeout(longPressTimeout);
    };

    // Compute sizes based on mode
    const isCompact = mode === "compact";
    const avatarSize = isCompact ? "h-6 w-6 min-w-6" : "h-8 w-8 min-w-8";
    const textSize = isCompact ? "text-xs" : "";
    const messagePadding = isCompact ? "px-3 py-1.5" : "px-4 py-2";

    return (
      <motion.div
        key={(message as any).localId || message.id}
        animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
        className={cn("group relative w-full", isUserMessage ? "items-end" : "items-start")}
        data-message-id={message.id}
        initial={
          shouldAnimate
            ? {
                opacity: 0,
                scale: 0.98,
                y: 8,
                x: isUserMessage ? 10 : -10,
              }
            : { opacity: 1, scale: 1, y: 0, x: 0 }
        }
        layout={!shouldAnimate} // Only use layout animation for existing messages
        transition={{
          duration: shouldAnimate ? 0.3 : 0.2,
          type: "spring",
          stiffness: 400,
          damping: 30,
          mass: 0.9,
          layout: {
            duration: 0.2,
            type: "spring",
            stiffness: 500,
            damping: 35,
          },
        }}
      >
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div
              ref={(node) => {
                bubbleNode = node;
              }}
              className={cn(
                "flex w-full rounded-xl px-2 transition-all duration-200",
                "hover:bg-muted/20 group-active:bg-muted/20",
                isUserMessage ? "justify-end" : "justify-start",
              )}
              onTouchCancel={handleTouchEnd}
              onTouchEnd={handleTouchEnd}
              onTouchStart={handleTouchStart}
            >
              {/* Avatar or spacer for non-user messages */}
              {!isUserMessage &&
                (isFirstInGroup ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Avatar
                          className={cn("ring-primary/20 mr-2 rounded-lg ring-1", avatarSize)}
                        >
                          <AvatarImage
                            alt={`${user.first_name} ${user.last_name}`}
                            src={
                              activeConversation?.members.find(
                                (member) => user.id === member.user.id,
                              )?.user?.avatar_path ||
                              "/placeholder.svg?height=32&width=32" ||
                              "/placeholder.svg"
                            }
                          />
                          <AvatarFallback>
                            {user.first_name.charAt(0)}
                            {user.last_name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                      </TooltipTrigger>
                      <TooltipContent side="top">{`${user.first_name} ${user.last_name}`}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <div className={cn("mr-2", isCompact ? "w-6 min-w-6" : "w-8 min-w-8")} />
                ))}

              {/* Message content with reduced padding */}
              <div
                className={cn(
                  "relative z-20 w-fit max-w-[min(96rem,_calc(100%_-_2rem))] rounded-md backdrop-blur-sm",
                  messagePadding,
                  (message as any).error
                    ? "text-destructive-foreground bg-red-500/5"
                    : isUserMessage
                      ? "bg-side-accent/50 text-accent-foreground"
                      : "bg-foreground/50 text-accent-foreground",
                  editingMessageId === message.id && "w-full! max-w-full!",
                  isLastInGroup && !isCompact && "pb-2", // Reduced bottom padding for icons
                  isLastInGroup && isCompact && "pb-1", // Reduced bottom padding for compact mode icons
                )}
              >
                {/* Message content or edit form */}
                {editingMessageId === message.id ? (
                  <div className="flex w-full flex-col gap-2">
                    <ScrollArea className="border-input bg-background/30 ring-offset-background focus-within:ring-ring box-border h-16 w-full rounded-lg border focus-within:ring-1">
                      <Textarea
                        ref={editTextareaRef}
                        className="bg-background/30 box-border [field-sizing:content] h-full w-full resize-none overflow-hidden border-none pt-2.5 pb-5 text-sm break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                        maxLength={5000}
                        value={editMessageContent}
                        onChange={(e) => setEditMessageContent(e.target.value)}
                        onKeyDown={handleEditKeyPress}
                      />
                    </ScrollArea>
                    <div className="flex justify-end gap-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              className={"hover:bg-sidebar/50"}
                              size="sm"
                              variant="ghost"
                              onClick={cancelEditing}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{t("common:cancel")}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              className={"hover:bg-sidebar/50"}
                              size="sm"
                              variant={"ghost"}
                              onClick={saveEditedMessage}
                            >
                              <Save className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{t("common:save")}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Error indicator for failed messages */}
                    {(message as any).error && (
                      <div className="mb-1 flex items-center gap-2">
                        <AlertCircle className={cn(isCompact ? "h-3 w-3" : "h-4 w-4")} />
                        <span className={cn("font-medium", isCompact ? "text-[10px]" : "text-xs")}>
                          {t("chat:errors.sendMessage")}
                        </span>
                      </div>
                    )}

                    <div className={cn("message-content max-w-full break-words", textSize)}>
                      {renderMessageTextWithLinks(message.message)}
                    </div>

                    {/* Display attachments - prioritize local blob URLs for immediate preview */}
                    {message.has_attachments && (
                      <div className="mt-2 flex flex-col gap-2">
                        {/* For messages with local files, show them immediately (both pending and confirmed) */}
                        {(message as any).files && (message as any).files.length > 0 ? (
                          <MediaGrid
                            key={`${(message as any).localId || message.id}-files-${(message as any).files.length}`}
                            className="mb-2"
                            items={(message as any).files.map((file: File, index: number) => ({
                              id: `${(message as any).localId || message.id}-${index}`,
                              file_name: file.name,
                              file_size: file.size,
                              file_type: file.type || "application/octet-stream",
                              url: URL.createObjectURL(file),
                            }))}
                          />
                        ) : (
                          /* For messages without local files, show server attachments */
                          message.attachments &&
                          message.attachments.length > 0 && (
                            <MediaGrid
                              className="mb-2"
                              items={message.attachments.map((attachment: any) => ({
                                id: attachment.id,
                                file_name: attachment.file_name,
                                file_size: attachment.file_size,
                                file_type: attachment.file_type || "application/octet-stream",
                                url: attachment.file_path,
                              }))}
                            />
                          )
                        )}
                      </div>
                    )}

                    {/* Link preview/embed */}
                    {!message.has_attachments &&
                      (() => {
                        const urls = extractUrls(message.message);

                        if (!urls || urls.length === 0) return null;

                        // Render only first URL preview to reduce noise
                        return <LinkPreviewCard url={urls[0]} />;
                      })()}

                    {/* Add read indicator in the bottom-right corner for the first message in the group (oldest) */}
                    {isLastInGroup && !isCompact && (
                      <>
                        {/* For current user's messages: read icon on left, edit icon on right */}
                        {/* For other users' messages: read icon on right, edit icon on left */}
                        {isUserMessage ? (
                          <>
                            {/* Read indicator (left side for current user) */}
                            <div className="absolute bottom-0.5 left-1 z-50 flex items-end">
                              {(() => {
                                const readByInfo = formatReadByInfo(message.read_by);
                                const hasReaders = readByInfo && readByInfo.length > 0;

                                return hasReaders ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div
                                          className="inline-flex cursor-pointer items-center"
                                          role="button"
                                          tabIndex={0}
                                          onClick={(e) => {
                                            if (isMobile) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                            }
                                          }}
                                        >
                                          <Check className="h-3 w-3 text-blue-500" />
                                          <Check className="-ml-1.5 h-3 w-3 text-blue-500" />
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent side="top">
                                        <div className="text-xs">
                                          <div className="mb-1 font-semibold">
                                            {t("chat:readBy")}
                                          </div>
                                          {readByInfo.map((reader, index) => (
                                            <div key={index} className="mb-1 last:mb-0">
                                              <div className="font-medium">{reader.name}</div>
                                              <div className="text-muted-foreground text-[10px]">
                                                {reader.timestamp}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <Check className="text-muted-foreground/50 h-3 w-3" />
                                );
                              })()}
                            </div>
                          </>
                        ) : (
                          <>
                            {/* Read indicator (right side for other users) */}
                            <div className="absolute right-1 bottom-0.5 flex items-end">
                              {(() => {
                                const readByInfo = formatReadByInfo(message.read_by);
                                const hasReaders = readByInfo && readByInfo.length > 0;

                                return hasReaders ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div
                                          className="inline-flex cursor-pointer items-center"
                                          role="button"
                                          tabIndex={0}
                                          onClick={(e) => {
                                            if (isMobile) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                            }
                                          }}
                                        >
                                          <Check className="h-3 w-3 text-blue-500" />
                                          <Check className="-ml-1.5 h-3 w-3 text-blue-500" />
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent side="top">
                                        <div className="text-xs">
                                          <div className="mb-1 font-semibold">
                                            {t("chat:readBy")}
                                          </div>
                                          {readByInfo.map((reader, index) => (
                                            <div key={index} className="mb-1 last:mb-0">
                                              <div className="font-medium">{reader.name}</div>
                                              <div className="text-muted-foreground text-[10px]">
                                                {reader.timestamp}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <Check className="text-muted-foreground/50 h-3 w-3" />
                                );
                              })()}
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {/* Compact mode: timestamp and actions below message */}
                    {isCompact && editingMessageId !== message.id && (
                      <div
                        className={cn(
                          "mt-1 flex items-center gap-2",
                          isUserMessage ? "justify-end" : "justify-start",
                        )}
                      >
                        {isUserMessage && (
                          <div className="opacity-0 transition-opacity group-hover:opacity-100">
                            {renderMessageActions(isUserMessage, message)}
                          </div>
                        )}
                        <div className="text-muted-foreground text-[10px]">
                          {formatInTimeZone(parseJSON(message.created_at), userTimezone, "p", {
                            locale: getDateFnsLocale(i18n.language),
                          })}
                        </div>

                        {/* Action buttons for user messages - shown on hover */}

                        {isLastInGroup && (
                          <div className={"flex items-center"}>
                            {/* For current user's messages: read icon on left, edit icon on right */}
                            {/* For other users' messages: read icon on right, edit icon on left */}
                            {isUserMessage ? (
                              <>
                                {/* Read indicator (left side for current user) */}
                                <div className="flex items-center">
                                  {(() => {
                                    const readByInfo = formatReadByInfo(message.read_by);
                                    const hasReaders = readByInfo && readByInfo.length > 0;

                                    return hasReaders ? (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div
                                              className="inline-flex cursor-pointer items-center"
                                              role="button"
                                              tabIndex={0}
                                              onClick={(e) => {
                                                if (isMobile) {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                }
                                              }}
                                            >
                                              <Check className="h-2.5 w-2.5 text-blue-500" />
                                              <Check className="-ml-1 h-2.5 w-2.5 text-blue-500" />
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <div className="text-xs">
                                              <div className="mb-1 font-semibold">
                                                {t("chat:readBy")}
                                              </div>
                                              {readByInfo.map((reader, index) => (
                                                <div key={index} className="mb-1 last:mb-0">
                                                  <div className="font-medium">{reader.name}</div>
                                                  <div className="text-muted-foreground text-[10px]">
                                                    {reader.timestamp}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    ) : (
                                      <Check className="text-muted-foreground/50 h-2.5 w-2.5" />
                                    );
                                  })()}
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Read indicator (right side for other users) */}
                                <div className="flex items-center">
                                  {(() => {
                                    const readByInfo = formatReadByInfo(message.read_by);
                                    const hasReaders = readByInfo && readByInfo.length > 0;

                                    return hasReaders ? (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div
                                              className="inline-flex cursor-pointer items-center"
                                              role="button"
                                              tabIndex={0}
                                              onClick={(e) => {
                                                if (isMobile) {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                }
                                              }}
                                            >
                                              <Check className="h-2.5 w-2.5 text-blue-500" />
                                              <Check className="-ml-1 h-2.5 w-2.5 text-blue-500" />
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <div className="text-xs">
                                              <div className="mb-1 font-semibold">
                                                {t("chat:readBy")}
                                              </div>
                                              {readByInfo.map((reader, index) => (
                                                <div key={index} className="mb-1 last:mb-0">
                                                  <div className="font-medium">{reader.name}</div>
                                                  <div className="text-muted-foreground text-[10px]">
                                                    {reader.timestamp}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    ) : (
                                      <Check className="text-muted-foreground/50 h-2.5 w-2.5" />
                                    );
                                  })()}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Avatar or spacer for user messages */}
              {isUserMessage &&
                (isFirstInGroup ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Avatar
                          className={cn("ring-primary/20 ml-2 rounded-lg ring-1", avatarSize)}
                        >
                          <AvatarImage
                            alt={`${currentUser.firstName} ${currentUser.lastName}`}
                            src={
                              currentUser.avatarUrl ||
                              "/placeholder.svg?height=32&width=32" ||
                              "/placeholder.svg"
                            }
                          />
                        </Avatar>
                      </TooltipTrigger>
                      <TooltipContent side="top">{`${currentUser.firstName} ${currentUser.lastName}`}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <div className={cn("ml-2", isCompact ? "w-6 min-w-6" : "w-8 min-w-8")} />
                ))}

              {/* Message actions and timestamp for both modes - always on hover */}
              <div
                className={cn(
                  "absolute flex gap-1 opacity-0 transition-opacity group-hover:opacity-100",
                  isUserMessage ? "left-2" : "right-2",
                  isCompact ? "top-1" : "top-1/2 -translate-y-1/2",
                )}
              >
                {editingMessageId !== message.id && !isCompact && (
                  <div
                    className={cn(
                      "text-muted-foreground flex items-center rounded-md px-1.5 py-0.5 text-xs backdrop-blur-sm",
                    )}
                  >
                    {formatInTimeZone(parseJSON(message.created_at), userTimezone, "p", {
                      locale: getDateFnsLocale(i18n.language),
                    })}
                  </div>
                )}

                {/* Only show action buttons here if not in compact mode or not a user message */}
                {isUserMessage &&
                  !editingMessageId &&
                  !isCompact &&
                  renderMessageActions(isUserMessage, message)}
              </div>
            </div>
          </ContextMenuTrigger>
          {isMobile && isUserMessage && (
            <ContextMenuContent>
              {isUserMessage && (
                <ContextMenuItem onClick={() => startEditingMessage(message.id, message.message)}>
                  <Edit2 className="mr-2 h-4 w-4" />
                  {t("common:edit")}
                </ContextMenuItem>
              )}
              {isUserMessage && (
                <ContextMenuItem variant="destructive" onClick={() => deleteMessage(message.id)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("common:remove")}
                </ContextMenuItem>
              )}
            </ContextMenuContent>
          )}
        </ContextMenu>
      </motion.div>
    );
  };

  // Function to render special groups as normal messages
  const renderSpecialGroupAsNormal = (group: any) => {
    return (
      <motion.div
        key={group.group_timestamp}
        layout
        animate={{ opacity: 1 }}
        className="mb-3 max-w-full"
        initial={{ opacity: 1 }}
        transition={{
          layout: {
            duration: 0.2,
            type: "spring",
            stiffness: 500,
            damping: 35,
          },
        }}
      >
        {/* Render each subgroup */}
        {group.subgroups.toReversed().map((subgroup: any) => {
          const isUserMessage = isCurrentUser(subgroup.user.id);

          return (
            <motion.div
              key={`${subgroup.group_timestamp}-${subgroup.user.id}`}
              layout
              className={cn("mb-2 flex flex-col", isUserMessage ? "items-end" : "items-start")}
              transition={{
                layout: {
                  duration: 0.15,
                  type: "spring",
                  stiffness: 600,
                  damping: 40,
                },
              }}
            >
              {/* Render messages in this subgroup */}
              {subgroup.messages.toReversed().map((message: any, msgIndex: number) => {
                const isLastInGroup = msgIndex === subgroup.messages.length - 1;
                const isFirstInGroup = msgIndex === 0;

                return renderMessage(
                  message,
                  isUserMessage,
                  isFirstInGroup,
                  isLastInGroup,
                  subgroup.user,
                );
              })}
            </motion.div>
          );
        })}
      </motion.div>
    );
  };

  // Function to render special groups as accordion trigger buttons for popup mode
  const renderSpecialGroupAsAccordionButton = (group: any) => {
    const formatTimeRange = () => {
      if (!group.special_data) return "";

      const startTime = new Date(group.special_data.start_time);
      const endTime = new Date(group.special_data.end_time);

      return `${startTime.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })} - ${endTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    };

    const totalMessages =
      group.subgroups?.reduce(
        (total: number, subgroup: any) => total + subgroup.messages.length,
        0,
      ) || 0;

    const handleLessonClick = (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('[role="button"]')) {
        // If clicking on the external link button, don't trigger group selection
        return;
      }
      onGroupSelect?.(group);
    };

    const handleLessonViewClick = () => {
      if (!group.special_data?.lesson_id || !activeConversation?.workspace_id) return;

      const lessonId = group.special_data.lesson_id;
      const path =
        userRole === "student"
          ? STUDENT_WORKSPACE_PATHS(activeConversation.workspace_id).LESSON_DETAILS
          : TUTOR_WORKSPACE_PATHS(activeConversation.workspace_id).LESSON_DETAILS;

      navigate(`${path}/${lessonId}/details`);
    };

    return (
      <motion.div
        key={group.group_timestamp}
        layout
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className={cn("max-w-full", mode === "compact" ? "mb-2" : "mb-3")}
        initial={{ opacity: 0, y: 8, scale: 0.98 }}
        transition={{
          duration: 0,
          layout: {
            duration: 0,
          },
        }}
      >
        <Accordion collapsible className="w-full" type="single">
          <AccordionItem className="border-none" value="lesson-messages">
            <Button
              className="bg-foreground/50 hover:bg-foreground/60 [&[data-state=open]]:bg-foreground/60 relative w-full cursor-pointer rounded-lg p-4 transition-all duration-300 ease-out hover:no-underline [&>svg]:transition-transform [&>svg]:duration-300 [&[data-state=open]]:rounded-b-none"
              variant="ghost"
              onClick={handleLessonClick}
            >
              <div className="flex w-full items-center justify-between pr-4">
                <div className="flex items-center gap-3">
                  <motion.div
                    className="bg-muted/20 flex items-center justify-center rounded-full p-2 backdrop-blur-sm"
                    transition={{ duration: 0 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <Icon
                      className="text-accent-foreground"
                      height={"1.5rem"}
                      icon="lucide:book-open"
                      width={"1.5rem"}
                    />
                  </motion.div>
                  <div className="text-left">
                    <h4 className="text-accent-foreground max-w-[200px] truncate text-sm font-semibold">
                      {group.special_data?.topic || t("lesson" as any)}
                    </h4>
                    <p className="text-muted-foreground text-xs">{formatTimeRange()}</p>
                  </div>
                </div>
                <div
                  className={`right-2 bottom-2 flex items-center gap-2 max-sm:absolute ${mode === "compact" && "absolute"}`}
                >
                  {group.special_data?.lesson_id && activeConversation?.workspace_id && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className="text-muted-foreground hover:text-accent-foreground flex h-8 w-8 items-center justify-center rounded-full p-0 transition-colors"
                            role="button"
                            tabIndex={0}
                            onClick={handleLessonViewClick}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {t("common:calendar.slot.viewLessonDetails")}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {mode === "full" && (
                    <motion.div
                      className="bg-muted/20 rounded-full px-3 py-1 backdrop-blur-sm max-sm:hidden"
                      transition={{ duration: 0 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <span className="text-muted-foreground text-xs font-medium">
                        {t("chat:messages", { count: totalMessages })}
                      </span>
                    </motion.div>
                  )}
                </div>
              </div>
            </Button>
          </AccordionItem>
        </Accordion>
      </motion.div>
    );
  };

  const renderLessonGroup = (group: any) => {
    const formatTimeRange = () => {
      if (!group.special_data) return "";

      const startTime = new Date(group.special_data.start_time);
      const endTime = new Date(group.special_data.end_time);
      const locale = getDateFnsLocale(i18n.language);

      const startFormatted = formatInTimeZone(startTime, userTimezone, "Pp", { locale });
      const endFormatted = formatInTimeZone(endTime, userTimezone, "p", { locale });

      return `${startFormatted} - ${endFormatted}`;
    };

    const totalMessages = group.subgroups.reduce(
      (total: any, subgroup: any) => total + subgroup.messages.length,
      0,
    );

    // Navigate to lesson details
    const handleLessonClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!group.special_data?.lesson_id || !activeConversation?.workspace_id) return;

      const lessonId = group.special_data?.lesson_id;
      const path =
        userRole === "student"
          ? STUDENT_WORKSPACE_PATHS(activeConversation?.workspace_id).LESSON_DETAILS
          : TUTOR_WORKSPACE_PATHS(activeConversation?.workspace_id).LESSON_DETAILS;

      navigate(`${path}/${lessonId}/details`);
    };

    return (
      <motion.div
        key={group.group_timestamp}
        layout
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className={cn("max-w-full", mode === "compact" ? "mb-2" : "mb-3")}
        initial={{ opacity: 0, y: 8, scale: 0.98 }}
        transition={{
          duration: 0,
          layout: {
            duration: 0,
          },
        }}
      >
        <Accordion collapsible className="w-full" type="single">
          <AccordionItem className="border-none" value="lesson-messages">
            <AccordionTrigger className="bg-foreground/50 hover:bg-foreground/60 [&[data-state=open]]:bg-foreground/60 relative rounded-lg p-4 transition-all duration-300 ease-out hover:no-underline [&>svg]:transition-transform [&>svg]:duration-300 [&[data-state=open]]:rounded-b-none">
              <div className="flex w-full items-center justify-between pr-4">
                <div className="flex items-center gap-3">
                  <motion.div
                    className="bg-muted/20 flex items-center justify-center rounded-full p-2 backdrop-blur-sm"
                    transition={{ duration: 0 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <Icon
                      className="text-accent-foreground"
                      height={"1.5rem"}
                      icon="lucide:book-open"
                      width={"1.5rem"}
                    />
                  </motion.div>
                  <div className="text-left">
                    <h4 className="text-accent-foreground max-w-[200px] truncate text-sm font-semibold">
                      {group.special_data?.topic || t("lesson" as any)}
                    </h4>
                    <p className="text-muted-foreground text-xs">{formatTimeRange()}</p>
                  </div>
                </div>
                <div
                  className={`right-2 bottom-2 flex items-center gap-2 max-sm:absolute ${mode === "compact" && "absolute"}`}
                >
                  {group.special_data?.lesson_id && activeConversation?.workspace_id && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className="text-muted-foreground hover:text-accent-foreground flex h-8 w-8 items-center justify-center rounded-full p-0 transition-colors"
                            role="button"
                            tabIndex={0}
                            onClick={handleLessonClick}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {t("common:calendar.slot.viewLessonDetails")}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {mode === "full" && (
                    <motion.div
                      className="bg-muted/20 rounded-full px-3 py-1 backdrop-blur-sm max-sm:hidden"
                      transition={{ duration: 0 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <span className="text-muted-foreground text-xs font-medium">
                        {t("chat:messages", { count: totalMessages })}
                      </span>
                    </motion.div>
                  )}
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden pt-0">
              <motion.div
                layout
                animate={{ opacity: 1 }}
                className="relative bg-transparent p-4"
                initial={{ opacity: 0 }}
                transition={{
                  duration: 0.3,
                  delay: 0.1,
                  ease: [0.23, 1, 0.32, 1],
                }}
              >
                {/* Animated gradient line */}
                <motion.div
                  animate={{ height: "100%" }}
                  className="bg-foreground/60 absolute top-0 left-0 w-0.5"
                  initial={{ height: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />

                {group.subgroups.toReversed().map((subgroup: any, index: number) => {
                  const isUserMessage = isCurrentUser(subgroup.user.id);

                  return (
                    <motion.div
                      key={`${subgroup.group_timestamp}-${subgroup.user.id}`}
                      layout
                      animate={{ opacity: 1, y: 0 }}
                      className={cn(
                        "mb-2 flex flex-col",
                        isUserMessage ? "items-end" : "items-start",
                      )}
                      initial={{ opacity: 0, y: 10 }}
                      transition={{
                        duration: 0.25,
                        delay: index * 0.05,
                        type: "spring",
                        stiffness: 450,
                        damping: 32,
                        layout: {
                          duration: 0.15,
                          type: "spring",
                          stiffness: 600,
                          damping: 40,
                        },
                      }}
                    >
                      {subgroup.messages.toReversed().map((message: any, msgIndex: number) => {
                        const isLastInGroup = msgIndex === subgroup.messages.length - 1;
                        const isFirstInGroup = msgIndex === 0;

                        return renderMessage(
                          message,
                          isUserMessage,
                          isFirstInGroup,
                          isLastInGroup,
                          subgroup.user,
                        );
                      })}
                    </motion.div>
                  );
                })}
              </motion.div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </motion.div>
    );
  };

  // Helper function to render a single pending message
  const renderPendingMessage = (
    pendingMsg: any,
    isFirstInGroup: boolean,
    isLastInGroup: boolean,
  ) => {
    const isCompact = mode === "compact";
    const avatarSize = isCompact ? "h-6 w-6 min-w-6" : "h-8 w-8 min-w-8";
    const textSize = isCompact ? "text-xs" : "";
    const messagePadding = isCompact ? "px-3 py-1.5" : "px-4 py-2";

    if (editingPendingMessageId === pendingMsg.id) {
      return (
        <motion.div
          key={pendingMsg.id}
          layout
          animate={{ opacity: 1, scale: 1, x: 0 }}
          className={cn("group relative flex w-full justify-end")}
          initial={{ opacity: 0, scale: 0.98, x: 5 }}
          transition={{
            duration: 0.25,
            type: "spring",
            stiffness: 450,
            damping: 32,
            mass: 0.8,
            layout: {
              duration: 0.15,
              type: "spring",
              stiffness: 600,
              damping: 40,
            },
          }}
        >
          <div
            className={cn(
              "bg-primary/70 w-full max-w-full rounded-lg",
              messagePadding,
              "shadow-md",
            )}
          >
            <ScrollArea className="border-input bg-background/30 ring-offset-background focus-within:ring-ring box-border h-16 w-full rounded-lg border focus-within:ring-1">
              <Textarea
                ref={editTextareaRef}
                className="bg-background/30 box-border [field-sizing:content] h-full w-full resize-none overflow-hidden border-none pt-2.5 pb-5 text-sm break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                maxLength={5000}
                value={editMessageContent}
                onChange={(e) => setEditMessageContent(e.target.value)}
                onKeyDown={handleEditKeyPress}
              />
            </ScrollArea>
            <div className="mt-2 flex justify-end gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="ghost" onClick={cancelEditing}>
                      {t("common:cancel")}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("common:cancel")}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" onClick={saveEditedMessage}>
                      <Save className="mr-1 h-3.5 w-3.5" />
                      {t("common:save")}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("common:save")} </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div
        key={pendingMsg.localId || pendingMsg.id}
        layout
        animate={{ opacity: 1, scale: 1, x: 0 }}
        className={cn("group relative w-full")}
        data-message-id={
          pendingMsg.status === "delivered" && pendingMsg.realMessageId
            ? pendingMsg.realMessageId
            : pendingMsg.id
        }
        initial={{ opacity: 0, scale: 0.98, x: 5 }}
        transition={{
          duration: 0.25,
          type: "spring",
          stiffness: 450,
          damping: 32,
          mass: 0.8,
          layout: {
            duration: 0.15,
            type: "spring",
            stiffness: 600,
            damping: 40,
          },
        }}
      >
        {/* Message wrapper with hover effect and reduced padding */}
        <div
          className={cn(
            "flex w-full rounded-xl px-2 py-1 transition-colors",
            "hover:bg-sidebar-accent/10 group-hover:bg-sidebar-accent/10",
            "justify-end",
          )}
        >
          {/* Message content with reduced padding */}
          {pendingMsg.status === "sending" || pendingMsg.status === "transitioning" ? (
            <div
              className={cn(
                "bg-side-accent/50 text-accent-foreground relative w-fit max-w-[min(96rem,_calc(100%_-_2rem))] rounded-md backdrop-blur-sm",
                messagePadding,
                "opacity-75", // Add tint for unconfirmed messages
              )}
            >
              <div className={cn("max-w-full break-words", textSize)}>{pendingMsg.content}</div>

              {/* Show file previews if there are files */}
              {pendingMsg.files.length > 0 && (
                <div className="mt-2 flex flex-col gap-2">
                  <MediaGrid
                    key={`${pendingMsg.localId || pendingMsg.id}-files-${pendingMsg.files.length}`}
                    className="mb-2"
                    items={pendingMsg.files.map((file: File, index: number) => ({
                      id: `${pendingMsg.localId || pendingMsg.id}-${index}`,
                      file_name: file.name,
                      file_size: file.size,
                      file_type: file.type || "application/octet-stream",
                      url: URL.createObjectURL(file),
                    }))}
                  />
                </div>
              )}

              {/* Show check mark instead of loading icon */}
              {isLastInGroup && !isCompact && (
                <div className="absolute right-2 bottom-1">
                  <Check className="text-muted-foreground/50 h-3 w-3" />
                </div>
              )}

              {/* Compact mode: timestamp below message */}
              {isCompact && (
                <div className="mt-1 flex items-center justify-end gap-2">
                  <div className="text-muted-foreground text-[10px]">
                    {formatInTimeZone(pendingMsg.timestamp, userTimezone, "p", {
                      locale: getDateFnsLocale(i18n.language),
                    })}
                  </div>
                  {isLastInGroup && <Check className="text-muted-foreground/50 h-2.5 w-2.5" />}
                </div>
              )}
            </div>
          ) : pendingMsg.status === "delivered" ? (
            <div
              className={cn(
                "bg-side-accent/50 text-accent-foreground relative w-fit max-w-[min(96rem,_calc(100%_-_2rem))] rounded-md backdrop-blur-sm",
                messagePadding,
              )}
            >
              <div className={cn("max-w-full break-words", textSize)}>{pendingMsg.content}</div>

              {/* Show attachments - prioritize local files if available, otherwise use server URLs */}
              {pendingMsg.files && pendingMsg.files.length > 0 ? (
                <div className="mt-2 flex flex-col gap-2">
                  <MediaGrid
                    key={`${pendingMsg.localId || pendingMsg.id}-files-${pendingMsg.files.length}`}
                    className="mb-2"
                    items={pendingMsg.files.map((file: File, index: number) => ({
                      id: `${pendingMsg.localId || pendingMsg.id}-${index}`,
                      file_name: file.name,
                      file_size: file.size,
                      file_type: file.type || "application/octet-stream",
                      url: URL.createObjectURL(file),
                    }))}
                  />
                </div>
              ) : (
                pendingMsg.deliveredData?.attachments &&
                pendingMsg.deliveredData.attachments.length > 0 && (
                  <div className="mt-2 flex flex-col gap-2">
                    <MediaGrid
                      className="mb-2"
                      items={pendingMsg.deliveredData.attachments.map((attachment: any) => ({
                        id: attachment.id,
                        file_name: attachment.file_name,
                        file_size: attachment.file_size,
                        file_type: attachment.file_type || "application/octet-stream",
                        url: attachment.file_path,
                      }))}
                    />
                  </div>
                )
              )}

              {/* Check mark for delivered status */}
              {isLastInGroup && !isCompact && (
                <div className="absolute right-2 bottom-1">
                  {(() => {
                    const readByInfo = formatReadByInfo(pendingMsg.deliveredData?.read_by);
                    const hasReaders = readByInfo && readByInfo.length > 0;

                    return hasReaders ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="inline-flex items-center">
                              <Check className="h-3 w-3 text-blue-500" />
                              <Check className="-ml-1.5 h-3 w-3 text-blue-500" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <div className="text-xs">
                              <div className="mb-1 font-semibold">{t("chat:readBy")}</div>
                              {readByInfo.map((reader, index) => (
                                <div key={index} className="mb-1 last:mb-0">
                                  <div className="font-medium">{reader.name}</div>
                                  <div className="text-muted-foreground text-[10px]">
                                    {reader.timestamp}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <Check className="text-muted-foreground/50 h-3 w-3" />
                    );
                  })()}
                </div>
              )}

              {/* Compact mode: timestamp below message */}
              {isCompact && (
                <div className="mt-1 flex items-center justify-end gap-2">
                  <div className="text-muted-foreground text-[10px]">
                    {formatInTimeZone(pendingMsg.timestamp, userTimezone, "p", {
                      locale: getDateFnsLocale(i18n.language),
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              className={cn(
                "text-destructive-foreground relative w-fit max-w-2xl rounded-md bg-red-500/5 backdrop-blur-sm",
                messagePadding,
              )}
            >
              <div className={"flex justify-between"}>
                <div className="mb-1 flex items-center gap-2">
                  <AlertCircle className={cn(isCompact ? "h-3 w-3" : "h-4 w-4")} />
                  <span className={cn("font-medium", isCompact ? "text-[10px]" : "text-xs")}>
                    {t("chat:errors.sendMessage")}
                  </span>
                </div>
              </div>
              <p className={textSize}>{pendingMsg.content}</p>

              {pendingMsg.files.length > 0 && (
                <div className="mt-2 flex flex-col gap-2">
                  <MediaGrid
                    key={`${pendingMsg.localId || pendingMsg.id}-files-${pendingMsg.files.length}`}
                    className="mb-2"
                    items={pendingMsg.files.map((file: File, index: number) => ({
                      id: `pending-error-${pendingMsg.id}-${index}`,
                      file_name: file.name,
                      file_size: file.size,
                      file_type: file.type || "application/octet-stream",
                      url: URL.createObjectURL(file),
                    }))}
                  />
                </div>
              )}

              {/* Compact mode: timestamp below message (no actions here) */}
              {isCompact && (
                <div className="mt-1 flex items-center justify-end gap-2">
                  <div className="text-muted-foreground text-[10px]">
                    {formatInTimeZone(pendingMsg.timestamp, userTimezone, "p", {
                      locale: getDateFnsLocale(i18n.language),
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Avatar or spacer for pending messages - always show to maintain alignment */}
          {isFirstInGroup ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Avatar className={cn("ring-primary/20 ml-2 ring-1", avatarSize)}>
                    <AvatarImage
                      alt={`${currentUser.firstName} ${currentUser.lastName}`}
                      src={currentUser.avatarUrl || "/placeholder.svg?height=32&width=32"}
                    />
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent side="top">{`${currentUser.firstName} ${currentUser.lastName}`}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <div className={cn("ml-2", isCompact ? "w-6 min-w-6" : "w-8 min-w-8")} />
          )}

          {/* Message actions and timestamp - for all states */}
          <div
            className={cn(
              "absolute flex gap-1 opacity-0 transition-opacity group-hover:opacity-100",
              "left-2",
              isCompact ? "top-1" : "top-1/2 -translate-y-1/2",
            )}
          >
            {!isCompact && (
              <div
                className={cn(
                  "text-muted-foreground flex items-center rounded-md px-1.5 py-0.5 text-xs backdrop-blur-sm",
                )}
              >
                {formatInTimeZone(pendingMsg.timestamp, userTimezone, "p", {
                  locale: getDateFnsLocale(i18n.language),
                })}
              </div>
            )}

            {/* Show actions for delivered messages */}
            {pendingMsg.status === "delivered" && pendingMsg.realMessageId && (
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className={cn(
                          "hover:bg-foreground active:bg-foreground text-accent-foreground edit-button rounded-md backdrop-blur-sm",
                          isCompact ? "h-5 w-5" : "h-6 w-6",
                        )}
                        size="icon"
                        variant="ghost"
                      >
                        <Edit2 className={isCompact ? "h-2.5 w-2.5" : "h-3 w-3"} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("common:edit")}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className={cn(
                          "hover:bg-foreground active:bg-foreground text-destructive delete-button rounded-md backdrop-blur-sm",
                          isCompact ? "h-5 w-5" : "h-6 w-6",
                        )}
                        size="icon"
                        variant="ghost"
                      >
                        <Trash2 className={isCompact ? "h-2.5 w-2.5" : "h-3 w-3"} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side={"left"}>{t("common:remove")}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}

            {/* Show actions for error messages */}
            {pendingMsg.status === "error" && renderPendingMessageActions(pendingMsg)}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <ScrollArea
      ref={scrollAreaRef}
      className="flex-1 p-3 *:max-w-full [&_>_div_>_div]:block! [&.loading-messages_*]:transition-none!"
      onScrollCapture={() => checkIfNearBottom()}
    >
      {isLoading ? (
        <></>
      ) : (
        <LayoutGroup>
          <div ref={messagesContainerRef} className="mx-auto space-y-3">
            {/* Loading indicator for fetching next page */}
            {isFetchingNextPage && (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="bg-foreground/50 mb-4 flex justify-center rounded-2xl p-4 backdrop-blur-xl"
                initial={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-3">
                  <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                  <p className="text-muted-foreground text-sm">{t("chat:loadingMoreMessages")}</p>
                </div>
              </motion.div>
            )}

            {/* No more messages indicator */}
            {!hasNextPage &&
              ((messages && messages.data.length > 0) ||
                (messages &&
                  messages.data.some((dayEntry: any) =>
                    dayEntry.groups.some(
                      (group: any) =>
                        (group.special_tag &&
                          group.subgroups &&
                          group.subgroups.some((subgroup: any) =>
                            subgroup.messages.some((msg: any) => msg.pending),
                          )) ||
                        (group.messages && group.messages.messages.some((msg: any) => msg.pending)),
                    ),
                  ))) && (
                <motion.div
                  animate={{ opacity: 1 }}
                  className="bg-foreground/50 mb-4 flex justify-center rounded-2xl p-3 backdrop-blur-xl"
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-muted-foreground text-sm">{t("chat:noMoreMessages")}</p>
                </motion.div>
              )}

            {messages && messages.data.length > 0 ? (
              messages.data.toReversed().map((dayEntry: DayEntry) => {
                // Add safety check for dayEntry structure
                if (!dayEntry || typeof dayEntry !== "object") {
                  console.warn("Invalid dayEntry received:", dayEntry);

                  return null;
                }

                const dateKey = dayEntry.date || new Date().toISOString().split("T")[0];

                return (
                  <div key={dateKey} className={cn("mb-4", mode === "compact" && "mb-2")}>
                    <div className={cn("relative", mode === "compact" ? "mb-3" : "mb-6")}>
                      <div className="absolute inset-0 flex items-center">
                        <div className="border-border/30 w-full border-t" />
                      </div>
                      <div className="relative flex justify-center">
                        <div
                          className={cn(
                            "bg-foreground/50 rounded-full backdrop-blur-xl",
                            mode === "compact" ? "px-3 py-1" : "px-4 py-2",
                          )}
                        >
                          <span
                            className={cn(
                              "text-muted-foreground font-medium",
                              mode === "compact" ? "text-xs" : "text-sm",
                            )}
                          >
                            {formatDate(dayEntry.date)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {dayEntry.groups.toReversed().map((group: MessageGroup) => {
                      // Check if this is a special group with subgroups
                      if (group.special_tag && group.subgroups) {
                        // Render special groups based on the mode and props
                        if (onGroupSelect) {
                          // Render as accordion button for popup mode
                          return renderSpecialGroupAsAccordionButton(group);
                        } else {
                          // Render special groups based on the renderSpecialGroupsAsNormal prop
                          return renderSpecialGroupsAsNormal
                            ? renderSpecialGroupAsNormal(group)
                            : renderLessonGroup(group);
                        }
                      }

                      // Handle regular groups
                      const isUserMessage = isCurrentUser(group.messages.user.id);
                      const { user } = group.messages;

                      return (
                        <motion.div
                          key={group.group_timestamp}
                          layout
                          animate={{ opacity: 1 }}
                          className={cn("max-w-full", mode === "compact" ? "mb-2" : "mb-3")}
                          initial={{ opacity: 1 }}
                          transition={{
                            layout: {
                              duration: 0.2,
                              type: "spring",
                              stiffness: 500,
                              damping: 35,
                            },
                          }}
                        >
                          {/* All messages in the group with reduced spacing */}
                          <motion.div
                            layout
                            className={cn(
                              "flex flex-col",
                              mode === "compact" ? "gap-0.5" : "gap-0.5",
                              isUserMessage ? "items-end" : "items-start",
                            )}
                            transition={{
                              layout: {
                                duration: 0.15,
                                type: "spring",
                                stiffness: 600,
                                damping: 40,
                              },
                            }}
                          >
                            {group.messages.messages.toReversed().map((message, msgIndex) => {
                              // Check if this is the first message in the group (last in the reversed array)
                              const isFirstInGroup = msgIndex === 0;
                              const isLastInGroup = msgIndex === group.messages.messages.length - 1;

                              // Check if this message is pending (unused but kept for future reference)

                              return renderMessage(
                                message,
                                isUserMessage,
                                isFirstInGroup,
                                isLastInGroup,
                                user,
                              );
                            })}
                          </motion.div>
                        </motion.div>
                      );
                    })}
                  </div>
                );
              })
            ) : messages &&
              messages.data.some((dayEntry: any) =>
                dayEntry.groups.some(
                  (group: any) =>
                    (group.special_tag &&
                      group.subgroups &&
                      group.subgroups.some((subgroup: any) =>
                        subgroup.messages.some((msg: any) => msg.pending),
                      )) ||
                    (group.messages && group.messages.messages.some((msg: any) => msg.pending)),
                ),
              ) ? (
              // Show pending messages with date header
              <div className={cn("mb-4", mode === "compact" && "mb-2")}>
                <div className={cn("relative", mode === "compact" ? "mb-3" : "mb-6")}>
                  <div className="absolute inset-0 flex items-center">
                    <div className="border-border/30 w-full border-t" />
                  </div>
                  <div className="relative flex justify-center">
                    <div
                      className={cn(
                        "bg-foreground/50 rounded-full backdrop-blur-xl",
                        mode === "compact" ? "px-3 py-1" : "px-4 py-2",
                      )}
                    >
                      <span
                        className={cn(
                          "text-muted-foreground font-medium",
                          mode === "compact" ? "text-xs" : "text-sm",
                        )}
                      >
                        {formatDate(new Date().toISOString().split("T")[0])}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Render pending messages */}
                {messages.data.map((dayEntry: any) =>
                  dayEntry.groups.map((group: any) => {
                    if (group.special_tag && group.subgroups) {
                      return group.subgroups.map((subgroup: any) =>
                        subgroup.messages.map((message: any) => {
                          if (message.pending) {
                            return renderPendingMessage(message, true, true);
                          }

                          return null;
                        }),
                      );
                    } else {
                      return group.messages.messages.map((message: any) => {
                        if (message.pending) {
                          return renderPendingMessage(message, true, true);
                        }

                        return null;
                      });
                    }
                  }),
                )}
              </div>
            ) : (
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                className={cn(
                  "bg-foreground/50 relative flex flex-col items-center justify-center overflow-hidden rounded-2xl backdrop-blur-xl",
                  mode === "compact" ? "h-48 p-4" : "h-96",
                )}
                initial={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
              >
                <div
                  className={cn(
                    "mb-6 flex items-center justify-center rounded-2xl bg-gradient-to-tr from-purple-400/50 to-indigo-500/50",
                    mode === "compact" ? "mb-3 h-12 w-12" : "h-20 w-20",
                  )}
                >
                  <Icon
                    className="text-muted-foreground"
                    height={mode === "compact" ? "1.5rem" : "2.5rem"}
                    icon="mdi:chat-outline"
                    width={mode === "compact" ? "1.5rem" : "2.5rem"}
                  />
                </div>
                <h3
                  className={cn(
                    "text-accent-foreground mb-2 font-semibold",
                    mode === "compact" ? "text-sm" : "text-xl",
                  )}
                >
                  {t("chat:noMessages")}
                </h3>
                <p
                  className={cn(
                    "text-muted-foreground max-w-md text-center",
                    mode === "compact" ? "text-xs" : "",
                  )}
                >
                  {t("chat:startConversation")}
                </p>
              </motion.div>
            )}
          </div>
        </LayoutGroup>
      )}
    </ScrollArea>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);

    check();
    window.addEventListener("resize", check);

    return () => window.removeEventListener("resize", check);
  }, []);

  return isMobile;
}
